import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  cover_image: "",
  tag: "rv",
  meta_description: "",
  content: "## Start writing here\n\nYour content in **markdown**.",
  published: false,
};

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/admin/blog/${id}`)
      .then((r) => setForm(r.data))
      .catch(() => {
        toast.error("Post not found.");
        nav("/admin/blog");
      })
      .finally(() => setLoading(false));
  }, [id, isNew, nav]);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title?.trim() || !form.content?.trim()) {
      toast.error("Title and content are required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const slug = form.slug?.trim() || form.title.toLowerCase().replace(/\s+/g, "-");
        const { data } = await api.post("/admin/blog", { ...form, slug });
        toast.success("Post created.");
        nav(`/admin/blog/${data.id}`);
      } else {
        await api.put(`/admin/blog/${id}`, form);
        toast.success("Saved.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (isNew) {
      setForm((s) => ({ ...s, published: !s.published }));
      return;
    }
    try {
      await api.put(`/admin/blog/${id}`, { published: !form.published });
      setForm((s) => ({ ...s, published: !s.published }));
      toast.success(form.published ? "Unpublished." : "Published.");
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  if (loading) return <div className="font-mono-accent text-sm">Loading…</div>;

  const inputCls =
    "w-full bg-white border-2 border-[#122A1E] px-4 py-3 font-body text-[#0A0E0C] focus:outline-none focus:border-[#FF5A00]";
  const labelCls = "font-mono-accent text-[10px] uppercase text-[#4A5550] mb-2 block tracking-[0.2em]";

  return (
    <div data-testid="admin-blog-editor">
      <Link
        to="/admin/blog"
        className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase text-[#4A5550] hover:text-[#FF5A00] mb-6"
      >
        <ArrowLeft size={14} /> All posts
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-accent text-[10px] uppercase text-[#4A5550]">
            ◆ {isNew ? "New Post" : "Edit Post"}
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mt-1">
            {form.title || "Untitled"}
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={togglePublish}
            className="inline-flex items-center gap-2 border-2 border-[#122A1E] px-4 py-3 font-display font-bold uppercase text-xs tracking-wider hover:bg-[#122A1E] hover:text-white"
            data-testid="editor-toggle-publish"
          >
            {form.published ? <EyeOff size={14} /> : <Eye size={14} />}
            {form.published ? "Unpublish" : "Publish"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 text-white font-display font-bold uppercase text-xs tracking-wider px-5 py-3 border-2 border-[#122A1E]"
            data-testid="editor-save"
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-5">
          <div>
            <label className={labelCls}>Title *</label>
            <input className={inputCls} value={form.title} onChange={update("title")} data-testid="editor-title" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Slug</label>
              <input className={inputCls} value={form.slug} onChange={update("slug")} placeholder="auto-from-title" data-testid="editor-slug" />
            </div>
            <div>
              <label className={labelCls}>Tag</label>
              <select className={inputCls} value={form.tag} onChange={update("tag")} data-testid="editor-tag">
                <option value="rv">RV</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="atv">ATV</option>
                <option value="snowmobile">Snowmobile</option>
                <option value="marine">Marine</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Excerpt</label>
            <textarea rows={2} className={`${inputCls} resize-none`} value={form.excerpt} onChange={update("excerpt")} data-testid="editor-excerpt" />
          </div>
          <div>
            <label className={labelCls}>Cover Image URL</label>
            <input className={inputCls} value={form.cover_image} onChange={update("cover_image")} data-testid="editor-cover" />
          </div>
          <div>
            <label className={labelCls}>Meta Description (SEO)</label>
            <textarea rows={2} className={`${inputCls} resize-none`} value={form.meta_description} onChange={update("meta_description")} data-testid="editor-meta" />
          </div>
          <div>
            <label className={labelCls}>Content (Markdown) *</label>
            <textarea
              rows={20}
              className={`${inputCls} font-mono text-sm leading-relaxed resize-y`}
              value={form.content}
              onChange={update("content")}
              data-testid="editor-content"
            />
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="font-mono-accent text-[10px] uppercase text-[#4A5550] mb-3">◆ Live Preview</div>
          <div className="border-2 border-[#122A1E] bg-[#EAEAE5] p-6 prose-mr lg:sticky lg:top-24">
            {form.cover_image && (
              <img src={form.cover_image} alt="" className="w-full h-40 object-cover border-2 border-[#122A1E] mb-4" />
            )}
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-3">{form.title || "Untitled"}</h2>
            {form.excerpt && <p className="text-[#4A5550] italic mb-3 border-l-4 border-[#FF5A00] pl-3">{form.excerpt}</p>}
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || ""}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
